/**
 * `ImageNotAvailable` — brand-styled "no image" placeholder.
 *
 * Single source of truth for the "image missing" SVG used by:
 *   - PDP `ImageGallery` (when neither PIM, Cylindo 3D, nor `imageGroups` resolve)
 *   - PLP `RangeProductTile` (when `getMasterTileImages` + `product.image` are all empty)
 *
 * The wrapper is split from the SVG so each consumer can size + position the placeholder to fit
 * its own container (PDP uses an aspect-ratio band; PLP uses a portrait tile cell).
 *
 * Accessibility: the wrapper is `role="img"` with a localized `aria-label`. The visible "Image
 * not available" caption inside the SVG is shown to sighted users; screen readers read the
 * `aria-label` instead and don't double-announce because the inner `<text>` is part of an `<svg>`
 * inside an element that already has `role="img"`.
 */
import React from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui';

const ImageNotAvailable = ({wrapperProps, svgProps}) => {
  const intl = useIntl();
  const label = intl.formatMessage({
    defaultMessage: 'Image not available',
    id: 'image_not_available.label',
  });

  return (
    <Box aria-label={label} role="img" {...wrapperProps}>
      <svg
        fill="none"
        height="300"
        viewBox="0 0 400 300"
        width="400"
        xmlns="http://www.w3.org/2000/svg"
        {...svgProps}
      >
        <rect fill="#F7F5F0" height="300" width="400" />
        <g opacity="0.35">
          <rect
            height="84"
            rx="4"
            stroke="#1B1A18"
            strokeWidth="2"
            width="104"
            x="148"
            y="85"
          />
          <circle cx="175" cy="112" r="9" stroke="#1B1A18" strokeWidth="2" />
          <path
            d="M149 153L175 127L197 149L213 133L251 169"
            stroke="#1B1A18"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </g>
        <text
          fill="rgba(27,26,24,0.40)"
          fontFamily="sans-serif"
          fontSize="14"
          fontWeight="500"
          textAnchor="middle"
          x="200"
          y="208"
        >
          {label}
        </text>
      </svg>
    </Box>
  );
};

ImageNotAvailable.propTypes = {
  /** Chakra-style props forwarded to the outer `Box` (sizing, bg, aspect ratio, …). */
  wrapperProps: PropTypes.object,
  /** Chakra-style props forwarded to the inner `<svg>` (max-width, object-fit, …). */
  svgProps: PropTypes.object,
};

ImageNotAvailable.defaultProps = {
  wrapperProps: undefined,
  svgProps: undefined,
};

export default ImageNotAvailable;
